import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, InputGroup } from 'react-bootstrap';
import { toastr } from 'Helpers/ToastHelper';
import CreatableSelect from 'react-select/creatable';
import { SettingsApiService } from 'Services/ApiServices';
import F2WCustomLogger from 'Services/F2WCustomLogger';
import DetailModalBase from 'Views/Settings/Modals/DetailModalBase';

const KeywordsDetailModal = (props) => {
  const { showEditModal, setShowEditModal, currentRowData, setRefreshComponent } = props;

  const [modalKeywordValues, setModalKeywordValues] = useState({});
  const [newLabel, setNewLabel] = useState('');
  const [newValue, setNewValue] = useState('');

  useEffect(() => {
    setModalKeywordValues({
      dropdownName: currentRowData.dropdownName,
      options: currentRowData.options,
      comments: currentRowData.comments || '',
    });
    setNewLabel('');
    setNewValue('');
    // eslint-disable-next-line
  }, [currentRowData]);

  const handleChangesKeywordValues = (selectedOptions) => {
    setModalKeywordValues((prev) => ({
      ...prev,
      options: selectedOptions || [],
    }));
  };

  const handleAddAirportOption = () => {
    if (!newLabel.trim() || !newValue.trim()) return;
    const exists = (modalKeywordValues.options || []).some((opt) => opt.value === newValue.trim());
    if (exists) {
      toastr.warning('Warning', 'Value already exists');
      return;
    }
    setModalKeywordValues((prev) => ({
      ...prev,
      options: [...(prev.options || []), { label: newLabel.trim(), value: newValue.trim() }],
    }));
    setNewLabel('');
    setNewValue('');
  };

  const handleSaveKeyword = () => {
    const payload = {
      dropdownName: modalKeywordValues.dropdownName || currentRowData.dropdownName,
      options: (modalKeywordValues.options || []).map((opt) => ({
        label: opt.label,
        value: opt.value,
      })),
      comments: modalKeywordValues.comments || '',
    };

    SettingsApiService.updateKeyword(payload)
      .then(() => {
        setShowEditModal(false);
        setRefreshComponent((prev) => prev + 1);
        toastr.success('Success', 'Keyword updated successfully');
      })
      .catch((error) => {
        F2WCustomLogger.error('Error updating keyword', error, {
          modal: 'KeywordsDetail',
          dropdownName: modalKeywordValues.dropdownName,
        });
      });
  };

  const handleHide = () => {
    setModalKeywordValues({});
    setShowEditModal(false);
  };

  const isAirport = modalKeywordValues.dropdownName?.toLowerCase() === 'airports';
  const isCurrency = modalKeywordValues.dropdownName?.toLowerCase() === 'currency';

  return (
    <DetailModalBase
      show={showEditModal}
      onHide={handleHide}
      title={`${modalKeywordValues.dropdownName} - Keywords Detail`}
      saveLabel="Save Changes"
      onSave={handleSaveKeyword}
      comments={modalKeywordValues.comments}
      onCommentsChange={(value) =>
        setModalKeywordValues((prev) => ({
          ...prev,
          comments: value,
        }))
      }
    >
      <Form.Group>
        <Form.Label>Keyword Name</Form.Label>
        <Form.Control type="text" value={modalKeywordValues.dropdownName || ''} disabled />
      </Form.Group>
      <Form.Group>
        <Form.Label>
          Options
          {(isAirport || isCurrency) && (
            <span className="text-muted ms-2" style={{ fontSize: '0.9em' }}>
              (Add with a label and a value)
            </span>
          )}
        </Form.Label>
        {isAirport || isCurrency ? (
          <>
            <Row className="mb-2">
              <Col>
                <InputGroup>
                  <Form.Control
                    placeholder="Name (Label)"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    aria-label="Label"
                  />
                  <Form.Control
                    placeholder="Code (Value)"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    aria-label="Value"
                  />
                  <Button variant="success" onClick={handleAddAirportOption}>
                    Add
                  </Button>
                </InputGroup>
              </Col>
            </Row>
            <CreatableSelect
              value={modalKeywordValues.options}
              onChange={handleChangesKeywordValues}
              isSearchable
              isClearable
              isMulti
              getOptionLabel={(option) => `${option.label} (${option.value})`}
              getOptionValue={(option) => option.value}
              placeholder="Select or remove airports..."
              // Disable manual creation for airports
              isValidNewOption={() => false}
              formatCreateLabel={() => null}
              styles={{
                menuList: (provided) => ({
                  ...provided,
                  maxHeight: 5 * 36,
                  overflowY: 'auto',
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  maxHeight: 5 * 38,
                  overflowY: 'auto',
                }),
              }}
            />
          </>
        ) : (
          <CreatableSelect
            value={modalKeywordValues.options}
            onChange={handleChangesKeywordValues}
            isSearchable
            isClearable
            isMulti
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            placeholder="Select or add options..."
            styles={{
              menuList: (provided) => ({
                ...provided,
                maxHeight: 5 * 36,
                overflowY: 'auto',
              }),
              valueContainer: (provided) => ({
                ...provided,
                maxHeight: 5 * 38,
                overflowY: 'auto',
              }),
            }}
          />
        )}
      </Form.Group>
    </DetailModalBase>
  );
};

KeywordsDetailModal.propTypes = {
  showEditModal: PropTypes.bool.isRequired,
  setShowEditModal: PropTypes.func.isRequired,
  currentRowData: PropTypes.object.isRequired,
  setRefreshComponent: PropTypes.func.isRequired,
};

export default KeywordsDetailModal;
